'use client';

import { createContext, useContext, type PropsWithChildren } from 'react';
import {
  GROUP_USERS,
  GROUP_CATEGORIES,
  getAllCategories,
  getAllGroupCodes,
  getCategoryByCode,
  getCategoryName,
  isCodeInCategory,
  isValidGroupCode,
  isAdmin,
  hasExtendedRights,
  type GroupCategory,
} from '@/lib/groups';

interface GroupsContextType {
  categories: typeof GROUP_USERS;
  getAllCategories: () => GroupCategory[];
  getAllGroupCodes: () => number[];
  getCategoryByCode: (code: number) => GroupCategory | null;
  getCategoryName: (code: number) => string;
  isCodeInCategory: (code: number, category: keyof typeof GROUP_CATEGORIES) => boolean;
  isValidGroupCode: (code: number) => boolean;
  isAdmin: (code: number) => boolean;
  hasExtendedRights: (code: number) => boolean;
}

const GroupsContext = createContext<GroupsContextType | undefined>(undefined);

/**
 * Provider для глобального доступа к словарю групп
 */
export function GroupsProvider({ children }: PropsWithChildren) {
  const value: GroupsContextType = {
    categories: GROUP_USERS,
    getAllCategories,
    getAllGroupCodes,
    getCategoryByCode,
    getCategoryName,
    isCodeInCategory,
    isValidGroupCode,
    isAdmin,
    hasExtendedRights,
  };

  return (
    <GroupsContext.Provider value={value}>
      {children}
    </GroupsContext.Provider>
  );
}

/**
 * Hook для использования словаря групп на любой странице
 */
export function useGroups() {
  const context = useContext(GroupsContext);
  if (context === undefined) {
    throw new Error('useGroups must be used within a GroupsProvider');
  }
  return context;
}
